import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { get, isEmpty, isEqual, isNil } from 'lodash';

import * as layerOps from '../../../api/Layers/layer-operations';

import { featurePoint2LatLng, featureGetZoom } from '../../../modules/geojson-utilities';

import LeafletMap from '../../components/LeafletMap';

let addOnlineBaseMap;
let customScale;
let customMapsAttribution;
let customZoom;
let customCollapseButton;
let drawSingleLayer;
let getMapCenter;
let L;

class MapContainerGeoImage extends Component {
  bottomLeftMarker = null;

  geoImageLayer = null;

  geoImageMap = null;

  geoImageMapComponent = React.createRef();

  topLeftMarker = null;

  topRightMarker = null;

  state = {
    clientModulesReady: false,
    isLeafletMapLoaded: get(this, 'geoImageMapComponent.current.state.isMapLoaded', false),
    isModalMapLoaded: false
  };

  componentDidMount() {
    const { clientModulesReady } = this.state;
    if (!clientModulesReady) this.loadClientModules();
  }

  async componentDidUpdate(prevProps) {
    const {
      bottomLeft,
      geoImageOpacity,
      geoImageURL,
      layerSubType,
      topLeft,
      topRight
    } = this.props;
    const { clientModulesReady, isLeafletMapLoaded, isModalMapLoaded } = this.state;
    const {
      bottomLeft: prevBottomLeft,
      geoImageOpacity: prevGeoImageOpacity,
      topLeft: prevTopLeft,
      topRight: prevTopRight
    } = prevProps;

    if (clientModulesReady && isLeafletMapLoaded && !isModalMapLoaded) this.initializeMap();

    if (isModalMapLoaded) {
      if (!isEqual(geoImageOpacity, prevGeoImageOpacity) && !isNil(this.geoImageLayer))
        this.geoImageLayer.setOpacity(geoImageOpacity / 100);

      if (!isEqual(topLeft, prevTopLeft) && !isEmpty(topLeft) && isNil(this.topLeftMarker)) {
        this.topLeftMarker = L.marker(topLeft, { draggable: true }).addTo(this.geoImageMap);
        this.topLeftMarker.on('drag dragend', this.repositionImage);
      }
      if (!isEqual(topRight, prevTopRight) && !isEmpty(topRight) && isNil(this.topRightMarker)) {
        this.topRightMarker = L.marker(topRight, { draggable: true }).addTo(this.geoImageMap);
        this.topRightMarker.on('drag dragend', this.repositionImage);
      }
      if (
        !isEqual(bottomLeft, prevBottomLeft) &&
        !isEmpty(bottomLeft) &&
        isNil(this.bottomLeftMarker)
      ) {
        this.bottomLeftMarker = L.marker(bottomLeft, { draggable: true }).addTo(this.geoImageMap);
        this.bottomLeftMarker.on('drag dragend', this.repositionImage);
      }
      if (
        !isEmpty(topLeft) &&
        !isEmpty(topRight) &&
        !isEmpty(bottomLeft) &&
        isNil(this.geoImageLayer)
      ) {
        this.geoImageLayer = await drawSingleLayer({
          layer: {
            layerSubType,
            url: geoImageURL,
            geoImageCorners: { topLeft, topRight, bottomLeft }
          },
          style: { rasterStyle: { opacity: geoImageOpacity } },
          leafletMap: this.geoImageMap,
          layerToDraw: this.geoImageLayer
        });
      }
    }
  }

  addPoint = event => {
    const {
      bottomLeft,
      canAddPoint,
      topLeft,
      topRight,
      changeLayerEditorStateProperty
    } = this.props;
    if (canAddPoint) {
      if (isEmpty(topLeft)) {
        changeLayerEditorStateProperty('topLeft', event.latlng);
      } else if (isEmpty(topRight)) {
        changeLayerEditorStateProperty('topRight', event.latlng);
      } else if (isEmpty(bottomLeft)) {
        changeLayerEditorStateProperty('bottomLeft', event.latlng);
      } else {
        event.preventDefault();
      }
    }
  };

  initializeMap = async () => {
    const {
      bottomLeft,
      geoImageOpacity,
      geoImageURL,
      layer,
      layerSubType,
      topLeft,
      topRight,
      userControlSetting
    } = this.props;

    let layerLocation = layerOps.getMapLocation(layer);
    if (isEmpty(layerLocation)) layerLocation = userControlSetting.defaultMapCenter;
    this.geoImageMap = get(this, 'geoImageMapComponent.current.leafletMap');
    this.geoImageMap.setView(
      featurePoint2LatLng(getMapCenter({ location: layerLocation })),
      featureGetZoom(layerLocation)
    );
    addOnlineBaseMap(this.geoImageMap);
    if (this.geoImageMap.attributionControl)
      this.geoImageMap.removeControl(this.geoImageMap.attributionControl);
    if (this.geoImageMap.zoomControl) this.geoImageMap.removeControl(this.geoImageMap.zoomControl);
    if (this.geoImageMap.collapseButton)
      this.geoImageMap.removeControl(this.geoImageMap.collapseButton);
    customCollapseButton(this.geoImageMap);
    customZoom(this.geoImageMap);
    customMapsAttribution(this.geoImageMap);
    customScale(this.geoImageMap);

    if (!isEmpty(topLeft) && !isEmpty(topRight) && !isEmpty(bottomLeft) && !isEmpty(geoImageURL)) {
      this.geoImageLayer = await drawSingleLayer({
        layer: {
          layerSubType,
          url: geoImageURL,
          geoImageCorners: { topLeft, topRight, bottomLeft }
        },
        style: { rasterStyle: { opacity: geoImageOpacity } },
        leafletMap: this.geoImageMap,
        layerToDraw: this.geoImageLayer
      });
      this.topLeftMarker = L.marker(topLeft, { draggable: true }).addTo(this.geoImageMap);
      this.topRightMarker = L.marker(topRight, { draggable: true }).addTo(this.geoImageMap);
      this.bottomLeftMarker = L.marker(bottomLeft, { draggable: true }).addTo(this.geoImageMap);

      this.topLeftMarker.on('drag dragend', this.repositionImage);
      this.topRightMarker.on('drag dragend', this.repositionImage);
      this.bottomLeftMarker.on('drag dragend', this.repositionImage);
    }

    this.geoImageMap.on('click', this.addPoint);

    this.setState({ isModalMapLoaded: true });
  };

  repositionImage = () => {
    const { changeLayerEditorStateProperty } = this.props;
    this.geoImageLayer.reposition(
      this.topLeftMarker.getLatLng(),
      this.topRightMarker.getLatLng(),
      this.bottomLeftMarker.getLatLng()
    );
    changeLayerEditorStateProperty('topLeft', this.topLeftMarker.getLatLng());
    changeLayerEditorStateProperty('topRight', this.topRightMarker.getLatLng());
    changeLayerEditorStateProperty('bottomLeft', this.bottomLeftMarker.getLatLng());
  };

  updateMapState = newState => this.setState({ isLeafletMapLoaded: newState });

  async loadClientModules() {
    if (Meteor.isClient) {
      ({
        addOnlineBaseMap,
        customScale,
        customMapsAttribution,
        customZoom,
        customCollapseButton,
        drawSingleLayer,
        getMapCenter
      } = await import('../../../modules/leaflet-operations'));
      L = await import('leaflet');
      this.setState({ clientModulesReady: true });
    }
  }

  render() {
    const { deployEnv } = this.props;
    return (
      <LeafletMap
        mapId="geoImageMap"
        headerType="5"
        ref={this.geoImageMapComponent}
        updateMapState={this.updateMapState}
        deployEnv={deployEnv}
      />
    );
  }
}

MapContainerGeoImage.propTypes = {
  bottomLeft: PropTypes.object.isRequired,
  canAddPoint: PropTypes.bool.isRequired,
  deployEnv: PropTypes.string.isRequired,
  geoImageOpacity: PropTypes.number.isRequired,
  geoImageURL: PropTypes.string.isRequired,
  layer: PropTypes.object.isRequired,
  layerSubType: PropTypes.string.isRequired,
  topLeft: PropTypes.object.isRequired,
  topRight: PropTypes.object.isRequired,
  changeLayerEditorStateProperty: PropTypes.func.isRequired,
  userControlSetting: PropTypes.object.isRequired
};

export default MapContainerGeoImage;
